import { useEffect, useRef, useState } from 'react';
import { RigidBody, RapierRigidBody } from '@react-three/rapier';
import { Group, Plane, Raycaster, Vector3 } from 'three';
import { multiplayer } from '../multiplayer';
import { useFrame } from '@react-three/fiber';

const hoverPlane = new Plane(new Vector3(0, 1, 0), -2);

const cardWidth = 2.5;
const cardHeight = 3.5;
const cardDepth = 0.1;

export function HostCard(props: { id: string }) {
  const { id } = props;
  const [held, setHeld] = useState(false);

  const body = useRef<RapierRigidBody>(null);
  const raycaster = useRef(new Raycaster());

  useEffect(() => multiplayer.packets.on('onClick', (data) => {
    if (data.objectId !== id) return;
    handleClick();
  }), [id])

  // TODO: fix tick rate for this? Maybe 60, 30, or 20 tps.
  // TODO: also use technique to optimize when sending snapshots like priority queue or something for fixed max bandwidth. you could use an physics object waking up as a trigger to prioritize it
  // TODO: batch updates to reduce network load
  useFrame(({ pointer, camera }) => {
    if (!body.current) return;
    multiplayer.send('objectSnapshot', {
      objectId: id,
      position: body.current.translation(),
    })

    if (held) {
      raycaster.current.setFromCamera(pointer, camera);
      const ray = raycaster.current.ray;
      const point = ray.intersectPlane(hoverPlane, new Vector3());
      if (point) {
        body.current.setTranslation({
          x: point.x,
          y: point.y,
          z: point.z
        }, true);
      }
    }
  })

  function handleClick() {
    if (!body.current) return;

    setHeld(prev => {
      body.current?.setBodyType(prev ? 0 : 2, true);
      return !prev;
    });

    body.current.applyImpulse({
      x: 0,
      y: 2,
      z: 0
    }, true);
  }

  return (
    <RigidBody position={[0, 2, 0]} ref={body} lockRotations={true} colliders="cuboid">
      <mesh onClick={handleClick} >
        <boxGeometry args={[cardWidth, cardDepth, cardHeight]} />

        {/* Card faces */}
        <CardMaterial />
      </mesh>
    </RigidBody>
  )
}

export function ClientCard(props: { id: string }) {
  // const { id } = props;
  const group = useRef<Group>(null);

  const [pos, updatePos] = useState<[number, number, number]>([0, 0, 0]);
  const targetPos = useRef({ x: 0, y: 0, z: 0 });


  useEffect(() => multiplayer.packets.on('objectSnapshot', (data) => {
    if (data.objectId !== props.id) return;
    targetPos.current = data.position;
  }), [props.id])


  useFrame((_, delta) => {
    if (!group.current) return;

    // Interpolate position and rotation
    const newPos = group.current.position.lerp(targetPos.current, delta * 10)
    updatePos([
      newPos.x,
      newPos.y,
      newPos.z
    ]);
  })

  function handleClick() {
    multiplayer.send('onClick', {
      objectId: props.id,
    })
  }

  return (
    <group position={pos} ref={group}>
      <mesh onClick={handleClick} >
        <boxGeometry args={[cardWidth, cardDepth, cardHeight]} />
        <CardMaterial />
      </mesh>
    </group>
  )
}

// Changed to default export for simpler importing
export default function Card(props: { id: string }) {
  const { id } = props;

  if (multiplayer.connection.isHost) {
    return <HostCard id={id} />
  }

  return <ClientCard id={id} />
}
// Component for card materials
function CardMaterial() {
  return (
    <meshStandardMaterial
      color={"#fff"}
      roughness={0.4}
    />
  );
}